import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  WaterDrop as FluidIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import {
  getAssessmentByClientID,
  createAssessment,
  deleteAssessmentID,
} from "../../../../../../api/services/observations";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditFluidModal from "./editFluidModal";

const FluidMonitor = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    value: "",
    notes: "",
    date_time: "",
    handover: false,
    manager: false,
  });
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [RowID, setRowID] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const assessmentType = "fluid";

  const profile = useUserProfile();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const resetForm = () => {
    setFormData({
      value: "",
      notes: "",
      date_time: "",
      handover: false,
      manager: false,
    });
    document.getElementById("fluid-form")?.reset();
  };

  const { data: assessments, isLoading } = useQuery(
    [QK.pulse_rates, clientID],
    () => getAssessmentByClientID(clientID, assessmentType)
  );

  const createMutation = useMutation(createAssessment, {
    onSuccess: () => {
      enqueueSnackbar("Fluid entry created successfully.", {
        variant: "success",
      });
      queryClient.invalidateQueries([QK.pulse_rates, clientID]);
      resetForm();
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to create fluid entry.",
        {
          variant: "error",
        }
      );
    },
  });

  const deleteMutation = useMutation(deleteAssessmentID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.pulse_rates, clientID]);
      enqueueSnackbar("Fluid entry deleted successfully", {
        variant: "success",
      });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete fluid entry", {
        variant: "error",
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedFormData = {
      ...formData,
      client: clientID,
      carer: profile?.id,
      assessment_type: assessmentType,
    };
    createMutation.mutate(updatedFormData);
  };

  const handleOpenModal = (id) => {
    setSelectedID(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedID(null);
  };

  const handleUpdate = (id) => {
    handleOpenModal(id);
  };

  const handleDelete = (ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    setShowConfirmModal(false);
    setRowID(null);
  };

  const handleConfirm = () => {
    if (RowID) {
      deleteMutation.mutate(RowID);
    }
  };

  const headers = [
    { key: "value", label: "Value" },
    { key: "notes", label: "Notes" },
    { key: "date_time", label: "Date & Time" },
    { key: "manager", label: "Manager" },
    { key: "handover", label: "Handover" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "value") {
      return `${item.value || 0} mL/hr`;
    }
    if (header.key === "date_time") {
      return item.date_time ? new Date(item.date_time).toLocaleString() : "-";
    }
    if (header.key === "manager") {
      return item.manager ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#D1FAE5",
            color: "#039855",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
          }}
        />
      );
    }
    if (header.key === "handover") {
      return item.handover ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#D1FAE5",
            color: "#039855",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
          }}
        />
      );
    }
    if (header.key === "notes") {
      return (
        <span
          style={{
            display: "block",
            maxWidth: 150,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.notes || "-"}
        </span>
      );
    }
    return item[header.key] || "-";
  };

  return (
    <Box>
      {/* Form Card */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FluidIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Add Fluid Entry
            </Typography>
            <Chip
              label="New"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: "#D1FAE5",
                color: "#039855",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit} id="fluid-form">
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <CustomInput
                  label="Fluid Rate (mL/hr) *"
                  name="value"
                  value={formData.value || ""}
                  onChange={handleChange}
                  type="number"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <CustomDateField
                  name="date_time"
                  label="Date & Time *"
                  value={formData.date_time || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <CustomTextArea
                  label="Notes"
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleChange}
                  rows={1}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <CustomCheckbox
                    label="Share with Manager"
                    name="manager"
                    checked={formData.manager || false}
                    onClick={handleCheckBox}
                  />
                  <CustomCheckbox
                    label="Handover"
                    name="handover"
                    checked={formData.handover || false}
                    onClick={handleCheckBox}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={createMutation.isLoading}
                    startIcon={<ClearIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      borderRadius: "8px",
                      borderColor: "#D0D5DD",
                      color: "#667085",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                        borderColor: "#98A2B3",
                      },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={createMutation.isLoading}
                    startIcon={
                      createMutation.isLoading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      bgcolor: "#2469F0",
                      px: 4,
                      "&:hover": {
                        bgcolor: "#1A5BD4",
                        boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                      },
                    }}
                  >
                    {createMutation.isLoading ? "Saving..." : "Save Fluid Entry"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Table Card */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Fluid Records
            </Typography>
            <Chip
              label={assessments?.length || 0}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: "#F2F4F7",
                color: "#475467",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={assessments || []}
            headers={headers}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={isLoading}
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this fluid entry?"
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EditFluidModal
        show={showModal}
        handleClose={handleCloseModal}
        selectedID={selectedID}
      />
    </Box>
  );
};

export default FluidMonitor;